import React, { useState } from 'react';
import {
    Modal, ModalHeader, ModalBody, ModalFooter,
    Button, Spinner,
} from 'reactstrap';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { BoldText, RegularText } from '../common/Text';
import { decideMismatch } from '../../services/api/prescription-mismatch';

const MISMATCH_TYPE_LABELS = {
    patient_name: 'Patient Name',
    doctor_name: 'Doctor Name',
    hospital_name: 'Hospital Name',
    appointment_date: 'Appointment Date',
};

const Section = styled.div`
    display: flex;
    flex-direction: row;
    gap: 1.5rem;
    margin-bottom: 0.75rem;
`;

const Field = styled.div`
    flex: 1;
`;

const Label = styled(RegularText)`
    font-size: 0.75rem;
    color: #8a8fa3;
    margin-bottom: 0.2rem;
`;

const Value = styled(BoldText)`
    font-size: 0.95rem;
    color: #2d2d2d;
`;

const Divider = styled.hr`
    border-color: #f0f0f0;
    margin: 0.75rem 0;
`;

const DecideModal = ({ mismatch, isOpen, onClose, onSuccess }) => {
    const [loading, setLoading] = useState(false);

    if (!mismatch) return null;

    const handleDecide = async (decision) => {
        setLoading(true);
        try {
            const correctedValue = (mismatch.mismatches || [])
                .map((m) => m.userCorrectedValue)
                .find((v) => v) || null;
            await decideMismatch(
                mismatch.mismatchId,
                decision,
                decision === 'APPROVED' ? correctedValue : null,
            );
            onClose();
            onSuccess();
            toast.success(`Mismatch ${decision === 'APPROVED' ? 'approved' : 'rejected'} successfully`);
        } catch (err) {
            toast.error(err.message || 'Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={loading ? undefined : onClose} centered>
            <ModalHeader className="bg-primary text-white" toggle={loading ? undefined : onClose}>
                Review Prescription Mismatch
            </ModalHeader>

            <ModalBody>
                <Section>
                    <Field>
                        <Label>Patient Name</Label>
                        <Value>{mismatch.patientName || '—'}</Value>
                    </Field>
                    <Field>
                        <Label>Action</Label>
                        <Value>{mismatch.action}</Value>
                    </Field>
                    <Field>
                        <Label>User Response</Label>
                        <Value>{mismatch.userResponse || '—'}</Value>
                    </Field>
                </Section>

                <Divider />

                {(mismatch.mismatches || []).map((m, i) => (
                    <div key={i}>
                        <Section>
                            <Field>
                                <Label>Mismatch Type</Label>
                                <Value>{MISMATCH_TYPE_LABELS[m.mismatchType] || m.mismatchType}</Value>
                            </Field>
                            <Field>
                                <Label>Prescription Value</Label>
                                <Value>{m.prescriptionValue || '—'}</Value>
                            </Field>
                            <Field>
                                <Label>System Value</Label>
                                <Value>{m.systemValue || '—'}</Value>
                            </Field>
                            {m.userCorrectedValue && (
                                <Field>
                                    <Label>User Corrected Value</Label>
                                    <Value>{m.userCorrectedValue}</Value>
                                </Field>
                            )}
                        </Section>
                        {i < mismatch.mismatches.length - 1 && <Divider />}
                    </div>
                ))}
            </ModalBody>

            <ModalFooter>
                <Button
                    color="success"
                    onClick={() => handleDecide('APPROVED')}
                    disabled={loading}
                >
                    {loading ? <Spinner size="sm" /> : 'Approve'}
                </Button>
                <Button
                    color="danger"
                    onClick={() => handleDecide('REJECTED')}
                    disabled={loading}
                >
                    {loading ? <Spinner size="sm" /> : 'Reject'}
                </Button>
                <Button
                    color="secondary"
                    onClick={onClose}
                    disabled={loading}
                >
                    Cancel
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default DecideModal;
